/* eslint-disable max-lines */
import { ReactNode } from 'react';

const Bar = ({ className = '' }: { className?: string }) => (
  <div className={`bg-onlight-bg-3 dark:bg-ondark-bg-3 rounded ${className}`} />
);

const Card = ({ children }: { children: ReactNode }) => (
  <div className="bg-onlight-bg-1 dark:bg-ondark-bg-1 rounded-lg p-4">
    {children}
  </div>
);

const SectionTitle = ({ width = 'w-32' }: { width?: string }) => (
  <Bar className={`mb-3 h-5 ${width}`} />
);

export function InvestigationViewSkeleton() {
  return (
    <div className="bg-hakimo-light-bg dark:bg-hakimo-dark-bg flex h-full overflow-hidden">
      <div className="flex min-w-0 flex-1 animate-pulse flex-col gap-2 overflow-y-auto pb-2">
        <div className="bg-onlight-bg-1 dark:bg-ondark-bg-1 flex h-16 flex-none items-center gap-3 px-2 py-2">
          <div className="flex max-w-[50%] flex-col gap-2 overflow-hidden">
            <Bar className="h-4 w-48" />
            <Bar className="h-3 w-20" />
          </div>
          <div className="flex flex-1 flex-col items-center justify-center gap-1">
            <Bar className="h-4 w-24" />
            <Bar className="h-3 w-40" />
          </div>
          <div className="flex items-center gap-2">
            <Bar className="h-8 w-8 rounded-md" />
            <Bar className="h-8 w-8 rounded-md" />
            <Bar className="h-8 w-20" />
            <Bar className="h-8 w-8 rounded-md" />
          </div>
        </div>

        <div className="bg-onlight-bg-1 dark:bg-ondark-bg-1 mx-auto flex min-h-[calc(100vh-5rem)] w-[95%] flex-none flex-col rounded-lg p-2">
          <div className="bg-onlight-bg-3 dark:bg-ondark-bg-3 relative flex-1 overflow-hidden rounded-lg" />
          <div className="ml-10 mt-4 flex items-center justify-between gap-2 pr-20">
            <div className="flex items-center gap-10">
              <Bar className="h-6 w-6 rounded-full" />
              <div className="flex items-center gap-2">
                <Bar className="h-6 w-6 rounded-full" />
                <Bar className="h-6 w-6 rounded-full" />
                <Bar className="h-6 w-6 rounded-full" />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Bar className="h-3 w-12" />
              <Bar className="h-2 w-32 rounded-full" />
            </div>
            <div className="flex items-center gap-2">
              <Bar className="h-3 w-20" />
              <Bar className="h-6 w-20 rounded-full" />
              <Bar className="h-6 w-20 rounded-full" />
            </div>
          </div>

          <div className="mb-10 mt-4 flex items-start gap-2 pl-2 pr-20 pt-2">
            <div className="w-1/6 pl-4">
              <Bar className="h-3 w-20" />
            </div>
            <div className="flex flex-grow flex-col gap-2 pr-1">
              <div className="flex justify-between">
                <Bar className="h-3 w-16" />
                <Bar className="h-3 w-16" />
                <Bar className="h-3 w-16" />
                <Bar className="h-3 w-16" />
              </div>
              <div className="bg-onlight-bg-3 dark:bg-ondark-bg-3 relative h-10 rounded">
                <div className="bg-onlight-bg-1/50 dark:bg-ondark-bg-1/40 absolute left-[12%] top-0 h-full w-[8%]" />
                <div className="bg-onlight-bg-1/50 dark:bg-ondark-bg-1/40 absolute left-[28%] top-0 h-full w-[14%]" />
                <div className="bg-onlight-bg-1/50 dark:bg-ondark-bg-1/40 absolute left-[55%] top-0 h-full w-[10%]" />
                <div className="bg-onlight-bg-1/50 dark:bg-ondark-bg-1/40 absolute left-[78%] top-0 h-full w-[12%]" />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-hakimo-light-bg dark:bg-hakimo-dark-bg mx-auto grid w-[95%] grid-cols-2 gap-2">
          <div className="flex flex-col gap-2">
            <Card>
              <SectionTitle width="w-40" />
              <div className="flex items-start gap-3">
                <Bar className="h-7 w-7 flex-none rounded-full" />
                <div className="flex-1 space-y-2">
                  <Bar className="h-3 w-full" />
                  <Bar className="h-3 w-5/6" />
                  <Bar className="h-3 w-3/4" />
                </div>
              </div>
            </Card>
            <Card>
              <SectionTitle width="w-40" />
              <div className="space-y-4">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="flex items-start gap-3">
                    <Bar className="h-8 w-8 flex-none rounded-full" />
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center justify-between">
                        <Bar className="h-3 w-1/3" />
                        <Bar className="h-3 w-16" />
                      </div>
                      <Bar className="h-3 w-full" />
                    </div>
                  </div>
                ))}
              </div>
            </Card>
            <Card>
              <SectionTitle width="w-40" />
              <div className="space-y-3">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Bar className="h-8 w-8 flex-none rounded-full" />
                      <div className="space-y-1">
                        <Bar className="h-3 w-32" />
                        <Bar className="h-3 w-24" />
                      </div>
                    </div>
                    <Bar className="h-6 w-6 rounded-full" />
                  </div>
                ))}
              </div>
            </Card>
            <Card>
              <SectionTitle width="w-32" />
              <Bar className="h-48 w-full" />
            </Card>
          </div>
          <Card>
            <SectionTitle width="w-40" />
            <div className="space-y-4">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="space-y-2">
                  <Bar className="h-4 w-1/2" />
                  <Bar className="h-3 w-full" />
                  <Bar className="h-3 w-5/6" />
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

export default InvestigationViewSkeleton;
